/** XREF — the External References palette, and the one operation this app can honestly offer.
 *
 * ⚠⚠ **AutoCAD RESOLVES A PATH; THIS CANNOT, AND SAYS SO.** AutoCAD looks a reference up at open
 * time against a search path, a project file and a network share. A stored path like
 * `C:\JOBS\2019\GRID.dwg` names a machine that is not this one, and every path in this office's
 * archive is absolute. So the status column is honest — "not found" is the normal answer — and
 * the action offered is BIND: point at the drawing this app already holds and pull the geometry
 * in, after which it is in this file and stays there.
 *
 * ⚠ Attaching a NEW reference is deliberately absent. It would store a path this app cannot read
 * at plot time, which is a promise it cannot keep.
 */
import { useEffect, useState } from "react";

import { loadXrefs, type XrefRow, type XrefTarget } from "./api";

export default function XrefDialog({ drawingId, onBind, onClose }: {
  drawingId: number;
  onBind: (block: string, fromId: number) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<XrefRow[] | null>(null);
  const [targets, setTargets] = useState<XrefTarget[]>([]);
  const [pick, setPick] = useState<Record<string, string>>({});

  useEffect(() => {
    loadXrefs(drawingId)
      .then((j) => { setRows(j.xrefs); setTargets(j.drawings); })
      .catch(() => setRows([]));
  }, [drawingId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 620, maxHeight: "88%", display: "flex", flexDirection: "column",
                    background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          External References
        </div>
        <div style={{ padding: 12, font: "11.5px system-ui", overflow: "auto", minHeight: 0 }}>
          {rows === null && <div style={{ color: "#9aa0a6" }}>Reading…</div>}
          {rows?.length === 0 && (
            <div style={{ color: "#9aa0a6", lineHeight: 1.6 }}>
              This drawing references no external files.
              <div style={{ marginTop: 6 }}>
                ⚠ Measured across this office's archive, about one drawing in twenty-five does.
              </div>
            </div>)}
          {rows?.map((r) => (
            <div key={r.name} data-testid={`xref-${r.name}`}
                 style={{ borderBottom: "1px solid #4a4d4f", padding: "8px 0",
                          display: "grid", gap: 5 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <b>{r.name}</b>
                <span style={{ color: "#9aa0a6" }}>
                  {r.overlay ? "overlay" : "attach"} · placed {r.inserts}×
                </span>
                {/* ⚠ The status column IS the palette. Loaded / Not found is what a drafter
                    opens this for, and "not found" is the normal answer here. */}
                <span style={{ marginLeft: "auto",
                               color: r.entities ? "#7bbf7b" : r.found ? "#c8a35a" : "#ff8f8f" }}>
                  {r.entities ? `bound · ${r.entities} objects`
                              : r.found ? "found on disk" : "not found"}
                </span>
              </div>
              <div style={{ color: "#9aa0a6", fontFamily: "monospace", fontSize: 11,
                            wordBreak: "break-all" }}>{r.path || "(no path stored)"}</div>
              {!r.entities && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ color: "#9aa0a6" }}>Bind from</span>
                  <select data-testid={`xref-pick-${r.name}`} value={pick[r.name] ?? ""}
                          onChange={(e) => setPick((p) => ({ ...p, [r.name]: e.target.value }))}
                          style={{ ...inp, flex: 1 }}>
                    <option value="">— choose a drawing —</option>
                    {targets.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.entities.toLocaleString()})
                      </option>))}
                  </select>
                  <button data-testid={`xref-bind-${r.name}`}
                          disabled={!pick[r.name]}
                          onClick={() => onBind(r.name, Number(pick[r.name]))}
                          style={{ ...btn, opacity: pick[r.name] ? 1 : 0.5 }}>Bind</button>
                </div>)}
            </div>))}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: "0 12px 12px" }}>
          <button style={btn} onClick={onClose} data-testid="xref-close">Close</button>
        </div>
      </div>
    </div>);
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "3px 6px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
